import React, {
  forwardRef,
  SyntheticEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { TooltipText } from '@app/core';
import { InputLabel } from '@app/form';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'block',

    height: theme.height.small,
    width: theme.marginBase * 45,

    cursor: 'text',

    borderRadius: theme.borderRadius.std,
    overflow: 'hidden',
  },
  iconContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: theme.height.small,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    fontSize: theme.iconSize.regular,
    color: theme.colors.inkTertiary,
  },
  input: {
    ...theme.fonts.caption,
    backgroundColor: theme.colors.white,

    height: '100%',
    width: '100%',

    paddingRight: theme.height.small + theme.marginBase * 2,
    paddingLeft: theme.marginBase * 2,

    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.colors.grayBorder,
    borderRadius: theme.borderRadius.std,

    transition: 'border-color 0.3s',
    '&:focus': {
      borderColor: theme.colors.blue,
    },
  },
  copyButton: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    width: theme.height.small,

    fontSize: theme.iconSize.regular,
    color: theme.colors.white,
  },
  blueButton: {
    backgroundColor: theme.colors.blueAccessible,
    '&:hover': {
      backgroundColor: theme.hoverColors.blueAccessible,
    },
    '&:active': {
      backgroundColor: theme.basicColors.blueAccessible.darken(0.2).hex(),
    },
  },
  purpleButton: {
    backgroundColor: theme.colors.purpleAccessible,
    '&:hover': {
      backgroundColor: theme.hoverColors.purpleAccessible,
    },
    '&:active': {
      backgroundColor: theme.basicColors.purpleAccessible.darken(0.2).hex(),
    },
  },
  accentButton: {
    backgroundColor: theme.colors.accentAccessible,
    '&:hover': {
      backgroundColor: theme.hoverColors.accentAccessible,
    },
    '&:active': {
      backgroundColor: theme.basicColors.accentAccessible.darken(0.2).hex(),
    },
  },
  leftSpace: {
    paddingLeft: theme.height.small,
  },
  withLabel: {
    width: '100%',
    height: theme.height.regular,
  },
  label: {
    marginTop: theme.marginBase * 3,
  },
}));

interface Props {
  link: string;
  className?: string;
  label?: string;
  secondaryLabel?: string;
  customIcon?: string;
  copyButtonVariant?: 'blue' | 'purple' | 'accent';
  hideIcon?: boolean;
}

export const SharableLink = forwardRef(
  (
    {
      link,
      className,
      label,
      customIcon = 'ri-link-m',
      copyButtonVariant = 'blue',
      hideIcon,
      secondaryLabel,
    }: Props,
    ref: any,
  ) => {
    const classes = useStyles();
    const newRef = useRef<HTMLInputElement | null>(null);
    const id = useId();
    const { t } = useTranslation('common');

    const inputRef = ref || newRef;
    const [tooltipOpened, setTooltipOpened] = useState(false);
    const closeTooltip = useCallback(
      () => setTooltipOpened(false),
      [setTooltipOpened],
    );
    const openTooltip = useCallback(
      () => setTooltipOpened(true),
      [setTooltipOpened],
    );

    const showTooltip = () => {
      openTooltip();
      setTimeout(() => {
        closeTooltip();
      }, 1000);
    };

    const onFocus = (e: SyntheticEvent<HTMLInputElement, FocusEvent>) => {
      e.currentTarget.select();
      document.execCommand('copy');
      showTooltip();
    };

    const onClick = () => inputRef.current && inputRef.current.focus();

    return (
      <TooltipText title={t('tooltip.copied')} open={tooltipOpened}>
        <label>
          {!!label && (
            <InputLabel
              label={label}
              htmlFor={id}
              className={classes.label}
              secondaryLabel={secondaryLabel}
            />
          )}
          <div
            className={classnames(
              classes.container,
              {
                [classes.withLabel]: !!label,
              },
              className,
            )}
          >
            {!hideIcon && (
              <div className={classes.iconContainer}>
                <i className={customIcon} />
              </div>
            )}
            <input
              className={classnames(classes.input, {
                [classes.leftSpace]: !hideIcon,
              })}
              onFocus={onFocus}
              value={link}
              ref={inputRef}
              readOnly
              id={id}
            />
            <button
              type="button"
              className={classnames(classes.copyButton, {
                [classes.purpleButton]: copyButtonVariant === 'purple',
                [classes.blueButton]: copyButtonVariant === 'blue',
                [classes.accentButton]: copyButtonVariant === 'accent',
              })}
              onClick={onClick}
            >
              <i className="ri-file-copy-line" />
            </button>
          </div>
        </label>
      </TooltipText>
    );
  },
);
